import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChatCircleDots, Phone, EnvelopeSimple, MapPin, Storefront, CaretDown, Clock, Globe, ShieldCheck, CaretRight, Lifebuoy } from "@phosphor-icons/react";
import { PageContainer } from "@/components/customer/PageContainer";
import { TicketDialog } from "@/components/customer/tickets/TicketDialog";
import { ASSETS } from "@/lib/assets";
import { api } from "@/lib/api";

function SupportTicketCta({ onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid="support-ticket-cta"
      className="w-full text-left group bg-[#141414] border border-neutral-800 rounded-2xl p-6 flex items-center gap-5 hover:border-[#73bf44]/60 transition-colors"
    >
      <div className="w-12 h-12 rounded-xl bg-[#73bf44]/10 border border-[#73bf44]/30 flex items-center justify-center shrink-0">
        <Lifebuoy size={24} className="text-[#73bf44]" weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-heading text-xl font-bold text-white">Lodge a Support Ticket</h3>
          <span className="text-[0.62rem] uppercase tracking-wider font-semibold text-sky-300 border border-sky-500/30 bg-sky-500/10 rounded-full px-2 py-0.5" data-testid="support-ticket-cta-badge">
            Technical assistance only
          </span>
        </div>
        <p className="text-sm text-neutral-400 mt-0.5">
          Describe the issue, attach photos or videos, and choose whether the OTM tech team calls or emails you back.
        </p>
      </div>
      <CaretRight size={22} className="text-neutral-500 group-hover:text-[#73bf44] transition-colors shrink-0" />
    </button>
  );
}

export default function ContactSupport() {
  const navigate = useNavigate();
  const [ticketOpen, setTicketOpen] = useState(false);
  const [dealers, setDealers] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [contact, setContact] = useState(null);

  useEffect(() => {
    api.get("/dealers").then(({ data }) => setDealers(data)).catch(() => {});
    api.get("/support-contact").then(({ data }) => setContact(data)).catch(() => {});
  }, []);

  const dealer = dealers.find((d) => d.id === selectedId) || null;

  return (
    <PageContainer
      overline="Contact"
      title="Contact Support"
      subtitle="Nick handles most technical questions instantly. For warranty claims, parts and safety-critical issues, reach the dealer you purchased your caravan from."
    >
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        {/* Submit a warranty claim */}
        <button
          onClick={() => navigate("/warranty")}
          data-testid="support-warranty-cta"
          className="w-full text-left group bg-gradient-to-r from-[#161a12] to-[#141414] border border-[#73bf44]/30 rounded-2xl p-6 flex items-center gap-5 hover:border-[#73bf44]/60 transition-colors"
        >
          <div className="w-12 h-12 rounded-xl bg-[#73bf44]/10 border border-[#73bf44]/30 flex items-center justify-center shrink-0">
            <ShieldCheck size={24} className="text-[#73bf44]" weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-xl font-bold text-white">Submit a Warranty Claim</h3>
            <p className="text-sm text-neutral-400 mt-0.5">
              Lodge a claim directly with On The Move — no need to go through your dealer. Have your repair quote and photos ready.
            </p>
          </div>
          <CaretRight size={22} className="text-neutral-500 group-hover:text-[#73bf44] transition-colors shrink-0" />
        </button>

        {/* Lodge a technical support ticket */}
        <SupportTicketCta onClick={() => setTicketOpen(true)} />
      </div>
      <TicketDialog open={ticketOpen} onOpenChange={setTicketOpen} standalone />

      <div className="grid md:grid-cols-2 gap-6">
        {/* Ask Nick first */}
        <div className="bg-gradient-to-b from-[#1a1a12] to-[#141414] border border-[#73bf44]/30 rounded-2xl p-7">
          <img src={ASSETS.nick} alt="Nick" className="w-16 h-16 rounded-full border border-neutral-700 mb-4" />
          <h3 className="font-heading text-2xl font-bold text-white mb-2">Ask Nick first</h3>
          <p className="text-neutral-400 text-sm mb-6">
            Nick can troubleshoot most issues using your caravan's exact specifications and OTM documentation — day or night.
          </p>
          <button
            onClick={() => navigate("/chat")}
            data-testid="support-ask-nick"
            className="inline-flex items-center gap-2 bg-[#73bf44] text-[#050505] rounded-full font-semibold px-6 py-3 hover:bg-[#5fa338] transition-colors"
          >
            <ChatCircleDots size={20} weight="fill" /> Chat with Nick
          </button>

          <div className="mt-7 pt-6 border-t border-neutral-800 space-y-3">
            {contact?.email && (
              <div className="flex items-start gap-3">
                <EnvelopeSimple size={18} className="text-[#73bf44] mt-0.5" weight="duotone" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-neutral-500">Email</div>
                  <a href={`mailto:${contact.email}`} data-testid="otm-email" className="text-white text-sm font-medium hover:text-[#73bf44] transition-colors break-all">{contact.email}</a>
                  {contact.note && <div className="text-xs text-neutral-600">{contact.note}</div>}
                </div>
              </div>
            )}
            {contact?.phone && (
              <div className="flex items-start gap-3">
                <Phone size={18} className="text-[#73bf44] mt-0.5" weight="duotone" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-neutral-500">Phone</div>
                  <a href={`tel:${contact.phone.replace(/[^+\d]/g, "")}`} data-testid="otm-phone" className="text-white text-sm font-medium hover:text-[#73bf44] transition-colors">{contact.phone}</a>
                </div>
              </div>
            )}
            {contact?.hours && (
              <div className="flex items-start gap-3">
                <Clock size={18} className="text-[#73bf44] mt-0.5" weight="duotone" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-neutral-500">Hours</div>
                  <div className="text-white text-sm font-medium" data-testid="otm-hours">{contact.hours}</div>
                </div>
              </div>
            )}
            {contact?.website && (
              <div className="flex items-start gap-3">
                <Globe size={18} className="text-[#73bf44] mt-0.5" weight="duotone" />
                <div>
                  <div className="text-xs uppercase tracking-wider text-neutral-500">Website</div>
                  <a href={contact.website} target="_blank" rel="noreferrer" data-testid="otm-website" className="text-white text-sm font-medium hover:text-[#73bf44] transition-colors break-all">{contact.website}</a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Select your selling dealer */}
        <div className="bg-[#141414] border border-neutral-800 rounded-2xl p-7">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-lg bg-black/40 flex items-center justify-center shrink-0">
              <Storefront size={20} className="text-[#73bf44]" weight="duotone" />
            </div>
            <h3 className="font-heading text-2xl font-bold text-white">Your selling dealer</h3>
          </div>
          <p className="text-neutral-400 text-sm mb-5">
            Warranty claims, parts and safety-critical issues are handled by the dealer you purchased from. Select your dealer to see their details.
          </p>

          <label className="text-sm text-neutral-400 mb-1.5 block">Select your selling dealer</label>
          <div className="relative">
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              data-testid="dealer-select"
              className="w-full appearance-none bg-black/40 border border-neutral-700 rounded-lg pl-3 pr-10 py-3 text-sm text-white outline-none focus:border-[#73bf44] transition-colors cursor-pointer"
            >
              <option value="">Choose a dealer…</option>
              {dealers.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.business_name}{d.city ? ` — ${d.city}, ${d.state}` : ""}
                </option>
              ))}
            </select>
            <CaretDown size={16} className="text-neutral-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>

          {dealer ? (
            <div data-testid="dealer-info" className="mt-5 bg-black/30 border border-[#73bf44]/20 rounded-xl p-5">
              <div className="font-heading text-lg font-bold text-white mb-4">{dealer.business_name}</div>
              <div className="space-y-3">
                {dealer.phone && (
                  <a href={`tel:${dealer.phone.replace(/[^+\d]/g, "")}`} data-testid="dealer-phone" className="flex items-start gap-3 group">
                    <Phone size={18} className="text-[#73bf44] mt-0.5" weight="duotone" />
                    <div>
                      <div className="text-xs uppercase tracking-wider text-neutral-500">Phone</div>
                      <div className="text-white text-sm font-medium group-hover:text-[#73bf44] transition-colors">{dealer.phone}</div>
                    </div>
                  </a>
                )}
                {dealer.email && (
                  <a href={`mailto:${dealer.email}`} data-testid="dealer-email" className="flex items-start gap-3 group">
                    <EnvelopeSimple size={18} className="text-[#73bf44] mt-0.5" weight="duotone" />
                    <div>
                      <div className="text-xs uppercase tracking-wider text-neutral-500">Warranty Email</div>
                      <div className="text-white text-sm font-medium group-hover:text-[#73bf44] transition-colors break-all">{dealer.email}</div>
                    </div>
                  </a>
                )}
                {(dealer.city || dealer.state) && (
                  <div className="flex items-start gap-3">
                    <MapPin size={18} className="text-[#73bf44] mt-0.5" weight="duotone" />
                    <div>
                      <div className="text-xs uppercase tracking-wider text-neutral-500">Location</div>
                      <div className="text-white text-sm font-medium">{[dealer.city, dealer.state].filter(Boolean).join(", ")}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-5 text-sm text-neutral-600 border border-dashed border-neutral-800 rounded-xl px-4 py-8 text-center">
              Select your dealer above to see their warranty contact details.
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
}
